class ConfiguracaoFaturamentoModal {
    static get $inject() {
        return ["$scope", "$timeout", "$filter", "gql", "Faturamento"]
    }

    constructor(scope, timeout, filter, gql, Faturamento) {
        this.apply = fn => scope.$apply(fn)
        this.timeout = timeout
        this.filter = filter
        this.gql = gql
        this.Faturamento = Faturamento

        this.close = this.close.bind(this)
    }

    $onInit() {
        this.empresa = this.resolve.empresa

        const promise = this.empresa.hasContrato
            ? this.loadEmpresaContrato()
            : this.loadUsuarioContratos()

        this.loadingPromise = promise
            .then(() => {
                const promises = [this.setupSubscriptions()]

                if (!this.contrato || !this.contrato.inadimplente) {
                    promises.push(this.loadFiliais())
                    promises.push(this.loadProdutos())
                }

                return Promise.all(promises)
            })
            .catch(console.error)
            .finally(() => this.apply())
    }

    $onDestroy() {
        this.unsubscribers.forEach(unsubscriber => unsubscriber())
    }

    saveContrato() {
        const mutation = gql`
            mutation AddEmpresaToContrato($contratoId: String!, $empresaId: String!) {
                contrato: addEmpresaToContrato(contratoId: $contratoId, empresaId: $empresaId) {
                    id
                    cliente {
                        id
                        nome
                        cpfCnpj
                    }
                    usuario { 
                        id
                        nome
                    }
                }
            }
        `

        const variables = {
            contratoId: this.contrato.id.toString(),
            empresaId: this.resolve.empresa.id.toString()
        }

        this.loadingPromise = this.gql
            .mutate(mutation, variables)
            .then(({ contrato }) => {
                if (contrato) {
                    this.contrato = contrato
                }
            })
            .catch(console.error)
            .finally(() => this.apply())
    }

    removeContrato() {
        Confirm.warning(
            "Você tem certeza que deseja desvincular esta empresa do contrato?",
            "A sua empresa não terá acesso aos produtos contratados até que seja incluída em um novo contrato.",
            {
                "Cancelar": angular.noop,
                "Sim, tenho certeza": () => {
                    const mutation = gql`
                        mutation RemoveEmpresaFromContrato($contratoId: String!, $empresaId: String!) {
                            contrato: removeEmpresaFromContrato(contratoId: $contratoId, empresaId: $empresaId) {
                                id
                            }
                        }
                    `

                    const variables = {
                        contratoId: this.contrato.id.toString(),
                        empresaId: this.resolve.empresa.id.toString()
                    }

                    this.gql.mutate(mutation, variables)
                        .then(({ contrato }) => {
                            if (contrato) {
                                this.contrato = null
                                this.filial = null
                            }
                        })
                        .catch(console.error)
                        .finally(() => this.apply())
                }
            })
    }

    selectFilial(filial) {
        this.filialSelectPromise = new Promise((resolve) => {
            this.filial = filial
            this.prepareProdutos()

            resolve()
        })
    }

    prepareProdutos() {
        this.produtos = this.produtosDisponiveis
            .filter(produto => this.filial.produtosContrataveis.includes(produto.id))
            .map(produto => {
                return {
                    ...produto,
                    contratado: this.filial.produtosContratados.includes(produto.id)
                }
            })
    }

    onProdutoChange(event, produto) {
        event.preventDefault()

        if (produto.contratado) {
            const produtos = this.produtos.filter(p => p.contratado && p.id != produto.id)

            Confirm.warning(
                "Você tem certeza que deseja remover este produto?",
                `
                    Esta operação irá remover automaticamente o produto <b>${produto.nome}</b> 
                    de seu contrato de assinatura. <br/> Todas as contratações possuem carência 
                    mínima de 30 dias. Sua próxima fatura conterá a última cobrança deste produto.
                `,
                {
                    "Cancelar": angular.noop,
                    "Sim, desejo remover": () => this.saveProdutos(produtos)
                })
        } else {
            const produtos = this.produtos.filter(p => p.contratado || p.id == produto.id)

            Confirm.warning(
                "Você tem certeza que deseja contratar este produto?",
                `
                    Esta operação irá incluir automaticamente o produto <b>${produto.nome}</b> 
                    na próxima fatura de seu contrato. <br/> Ao prosseguir, você concorda 
                    com os nossos termos de uso e política de privacidade.
                `,
                {
                    "Cancelar": angular.noop,
                    "Sim, desejo contratar": () => this.saveProdutos(produtos)
                })
        }
    }

    saveProdutos(produtos) {
        const mutation = gql`
            mutation CreateProdutosContratados($filialId: String!, $produtos: [String]!) {
                contratados: createProdutosContratados(filialId: $filialId, produtos: $produtos) {
                    id
                    produtos
                }
            }
        `

        const variables = {
            filialId: this.filial.id.toString(),
            produtos: produtos.map(p => p.id)
        }

        this.produtosForm.saveSuccess = false

        this.produtosSavePromise = this.gql.mutate(mutation, variables)
            .then(({ contratados: { produtos } }) => {
                this.filial.produtosContratados = produtos

                this.timeout(() => {
                    this.produtosForm.saveSuccess = true
                    this.timeout(() => { if (this.produtosForm) this.produtosForm.saveSuccess = false }, 2000)
                }, 500)
            })
            .catch(console.error)
            .finally(() => this.apply())
    }

    loadUsuarioContratos() {
        const query = gql`
            query ContratosList {
                usuarioAtual {
                    id
                    contratos {
                        id
                        diasAcessoRestantes
                        cliente {
                            id
                            nome
                            cpfCnpj
                        }
                        usuario {
                            id
                            nome
                        }
                    }
                }
            }
        `

        return this.gql
            .query(query)
            .then(({ usuarioAtual: { contratos } }) => {
                this.contratos = contratos
                    .map(contrato => {
                        contrato.inadimplente = contrato.diasAcessoRestantes && contrato.diasAcessoRestantes < 1

                        return contrato
                    })
                    .sortBy(contrato => contrato.nome)
            })
    }

    loadEmpresaContrato() {
        const query = gql`
            query EmpresaContrato {
                empresa: partialEmpresa(id: ${this.empresa.id}) {
                    id
                    contrato {
                        id
                        diasAcessoRestantes
                        cliente {
                            id
                            nome
                            cpfCnpj
                        }
                        usuario {
                            id
                            nome
                        }
                    }
                }
            }
        `

        return this.gql
            .query(query)
            .then(({ empresa: { contrato } }) => {
                this.contrato = contrato
                this.contrato.inadimplente = contrato.diasAcessoRestantes && contrato.diasAcessoRestantes < 1
            })
    }

    loadFiliais() {
        const query = gql`
            query Filiais {
                empresa(id: ${this.empresa.id}) {
                    id
                    filiais(ativa: true) {
                        id
                        razaoSocial
                        cnpj
                        ativa
                        produtosContrataveis
                        contratados: produtosContratados {
                            id
                            produtos
                        }
                        cidade {
                            id
                            nome
                            estado {
                                id
                                sigla
                            }
                        }
                    }
                }
            }
        `

        return this.gql
            .query(query)
            .then(({ empresa }) => {
                this.filiais = empresa.filiais
                    .map(({ contratados, ...filial }) => {
                        return {
                            ...filial,
                            produtosContratados: contratados ? contratados.produtos : []
                        }
                    })
                    .sortBy(filial => filial.cnpj)
            })
    }

    async loadProdutos() {
        this.produtosDisponiveis = await this.Faturamento.listProdutos()
    }

    setupSubscriptions() {
        this.unsubscribers = [
            this.subscribeToEmpresaAdicionadaAoContrato(),
            this.subscribeToEmpresaRemovidaDoContrato(),
            this.subscribeToProdutosContratadosCreated()
        ]
    }

    subscribeToEmpresaAdicionadaAoContrato() {
        const subscription = `
            subscription($empresaId: String!) {
                contrato: empresaAdicionadaAoContrato(empresaId: $empresaId) {
                    id
                    cliente {
                        id
                        nome
                        cpfCnpj
                    }
                    usuario {
                        id
                        nome
                    }
                }
            }
        `

        const variables = { empresaId: this.empresa.id.toString() }

        const callback = ({ data: { contrato } }) => {
            this.apply(() => this.contrato = contrato)
        }

        return this.gql.subscribe(subscription, variables, callback)
    }

    subscribeToEmpresaRemovidaDoContrato() {
        const subscription = `
            subscription($empresaId: String!) {
                contrato: empresaRemovidaDoContrato(empresaId: $empresaId) {
                    id
                }
            }
        `

        const variables = { empresaId: this.empresa.id.toString() }

        const callback = () => this.apply(() => {
            this.contrato = null
            this.loadingPromise = this.loadUsuarioContratos()
        })

        return this.gql.subscribe(subscription, variables, callback)
    }

    subscribeToProdutosContratadosCreated() {
        const subscription = `
            subscription ProdutosContratadosCreated($empresaId: String!) {
                contratados: produtosContratadosCreated(empresaId: $empresaId) {
                    id
                    produtos
                    filial {
                        id
                    }
                }
            }
        `

        const variables = { empresaId: this.empresa.id.toString() }

        const callback = ({ data: { contratados: { produtos, filial } } }) => {
            this.apply(() => {
                this.filiais.find(f => f.id == filial.id).produtosContratados = produtos
                this.prepareProdutos()
            })
        }

        return this.gql.subscribe(subscription, variables, callback)
    }

    highlight(search, contrato) {
        const cpfCnpj = this.filter('cpfCnpj')(contrato.cliente.cpfCnpj)
        const documento = contrato.cliente.cpfCnpj.length == 11 ? "CPF" : "CNPJ"

        const textoInadimplente = contrato.inadimplente ? ' (<i>Inadimplente</i>)' : ''
        const text = `${contrato.cliente.nome} - ${documento}: ${cpfCnpj}${textoInadimplente}`

        return search.split(' ').reduce(this.filter('highlight'), text)
    }

    close() {
        this.closeModal({ $value: { hasContrato: !!this.contrato } })
    }
}

angular.module("eficienciaFiscalModule")
    .component("configuracaoFaturamentoModal", {
        controller: ConfiguracaoFaturamentoModal,
        bindings: {
            closeModal: '&close',
            resolve: '<'
        },
        template: `
            <modal modal-title="Configuração de faturamento" close="$ctrl.close">
                <modal-body>
                    <loading promise="$ctrl.loadingPromise">
                        <div class="main" ng-if="!$ctrl.contrato && !$ctrl.contratos.length">
                            <div class="row">
                                <highlighted class="col-xs-12">
                                    <main>Você não possui nenhum contrato cadastrado.</main>
                                    <secondary>
                                        <a href="/contratos/novo" ng-click="$ctrl.close()">Clique aqui</a> 
                                        para abrir a tela de cadastro de contratos.
                                    </secondary>
                                </highlighted>
                            </div>
                        </div>

                        <div class="main" ng-if="$ctrl.contrato || $ctrl.contratos.length">
                            <div class="row">
                                <form name="$ctrl.contratoForm">
                                    <promise-tracker promise="$ctrl.loadingPromise" active="isLoading">
                                        <div class="col-xs-12" style="display: flex;">
                                            <ui-select
                                                theme="bootstrap" 
                                                name="contrato" 
                                                ng-model="$ctrl.contrato" 
                                                ng-change="$ctrl.saveContrato()"
                                                ng-disabled="$ctrl.contrato"
                                                ng-hide="isLoading"
                                                style="width:100%;"
                                                required>
                                                <ui-select-match placeholder="Clique aqui para selecionar um contrato.">
                                                    {{$select.selected.cliente.nome}} - 
                                                    {{$select.selected.cliente.cpfCnpj.length == 11 ? "CPF" : "CNPJ"}}: 
                                                    {{$select.selected.cliente.cpfCnpj | cpfCnpj}}
                                                    - Responsável: {{$select.selected.usuario.nome}}
                                                </ui-select-match>
                                                <ui-select-choices
                                                    repeat="contrato in $ctrl.contratos | filter: $select.search"
                                                    ui-disable-choice="contrato.inadimplente">
                                                    <div ng-bind-html="$ctrl.highlight($select.search, contrato)"></div>
                                                </ui-select-choices>
                                            </ui-select>

                                            <div
                                                class="btn btn-default"
                                                style="margin-left: 3px;"
                                                ng-if="$ctrl.contrato && $ctrl.contrato.usuario.id == $ctrl.resolve.usuarioId"
                                                ng-click="$ctrl.removeContrato()">
                                                <i class="fa fa-trash"></i>
                                            </div>
                                        </div>
                                    </promise-tracker>
                                </form>
                            </div>

                            <hr/>

                            <div class="row">
                                <div ng-if="!$ctrl.contrato">
                                    <highlighted>
                                        <main>Nenhum contrato selecionado.</main>
                                        <secondary>
                                            Selecione um contrato na opção acima. <br/> 
                                            Isso irá vincular a empresa atual ao perfil de faturamento cadastrado no contrato. <br/>
                                        </secondary>
                                    </highlighted>
                                </div>

                                <div ng-if="$ctrl.contrato && $ctrl.contrato.inadimplente">
                                    <highlighted>
                                        <main>O contrato desta empresa está inadimplente.</main>
                                        <secondary>
                                            <a href="/contratos/{{ $ctrl.contrato.id }}" ng-click="$ctrl.close()">
                                                Clique aqui 
                                            </a> 
                                            para acessar a página de cadastro do contrato.
                                        </secondary>
                                    </highlighted>
                                </div>

                                <div ng-if="$ctrl.contrato && !$ctrl.contrato.inadimplente">
                                    <form name="$ctrl.produtosForm">
                                        <promise-tracker promise="$ctrl.produtosSavePromise" active="isProdutosSaving">
                                            <div class="configuracao-container">
                                                <div class="panel">
                                                    <h3>Filiais</h3>

                                                    <div class="filiais" style="margin-right: 8px;">
                                                        <div 
                                                            ng-repeat="filial in $ctrl.filiais | filter: $ctrl.search" 
                                                            ng-click="$ctrl.selectFilial(filial)"
                                                            ng-class="{'filial-selected': $ctrl.filial.id == filial.id}"
                                                            class="filial"
                                                            style="display: flex;"
                                                            title="{{ filial.razaoSocial }}">
                                                            <div>
                                                                {{ $index + 1 }}) 
                                                                {{ (filial.cnpj | cpfCnpj).slice(11) }} | 
                                                                {{ filial.cidade.nome }}/{{ filial.cidade.estado.sigla }} 
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="divider" style="height: 95%"></div>

                                                <div class="panel">
                                                    <h3>Produtos contratados</h3>

                                                    <div ng-if="!$ctrl.filial" class="panel-content">
                                                        <h1 style="text-align: center; margin: 60px 0 60px">
                                                            <small>Selecione uma filial na lista à esquerda.</small>
                                                        </h1>
                                                    </div>

                                                    <div ng-if="$ctrl.filial" style="margin-left: 8px;">
                                                        <loading promise="$ctrl.filialSelectPromise">
                                                            <div style="margin-bottom: 10px">
                                                                <label>
                                                                    <b>CNPJ: </b>
                                                                    {{ $ctrl.filial.cnpj | cpfCnpj }}
                                                                </label>

                                                                <br/>

                                                                <label>
                                                                    <b>Razão social: </b>
                                                                    {{ $ctrl.filial.razaoSocial }}
                                                                </label>
                                                            </div>

                                                            <div ng-repeat="produto in $ctrl.produtos">
                                                                <label>
                                                                    <input
                                                                        type="checkbox"
                                                                        class="checkbox"
                                                                        ng-click="$ctrl.onProdutoChange($event, produto)"
                                                                        ng-checked="produto.contratado"
                                                                        ng-disabled="isProdutosSaving">
                                                                    <span>{{ produto.nome }}</span>
                                                                </label>
                                                            </div>
                                                        </loading>
                                                    </div>

                                                    <div ng-if="$ctrl.filial" style="margin: 10px 8px;">
                                                        <div
                                                            ng-show="isProdutosSaving && !$ctrl.produtosForm.saveSuccess"
                                                            class="fade-animation">
                                                            <i class="fa fa-circle-o-notch fa-spin"></i>
                                                            Salvando...
                                                        </div>

                                                        <div
                                                            ng-show="$ctrl.produtosForm.saveSuccess && !isProdutosSaving"
                                                            class="fade-animation text-success">
                                                            <i class="fa fa-check"></i> 
                                                            Salvo com sucesso!
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </promise-tracker>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </loading>
                </modal-body>
                <modal-footer>
                    <button class="btn btn-default" type="button" ng-click="$ctrl.close()">Fechar</button>
                </modal-footer>
            </modal>
        `
    })
